import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { leadService } from '../../services/leadService';
import { LeadStatus, LeadSubStage } from '../../types/lead';
import { LEAD_STATUS_CHANGE_OPTIONS, formatLeadStatusLabel } from '../../constants/pipelineStatuses';
import LoadingSpinner from '../UI/LoadingSpinner';

interface LeadStatusChangeMenuProps {
  leadId: string;
  campaignId: string;
  currentStatus?: string | null;
  subStageName?: string | null;
  subStageId?: string | null;
  onUpdated: (patch: { status: string; subStageId?: string | null; subStageName?: string | null }) => void;
  onClose: () => void;
  /** When set to follow_up, optionally prompt follow-up after save */
  onRequestFollowUp?: () => void;
}

const LeadStatusChangeMenu: React.FC<LeadStatusChangeMenuProps> = ({
  leadId,
  campaignId,
  currentStatus,
  subStageName,
  subStageId,
  onUpdated,
  onClose,
  onRequestFollowUp,
}) => {
  const [mainStatus, setMainStatus] = useState<LeadStatus>(
    (currentStatus as LeadStatus) || 'interested'
  );
  const [selectedSubStageId, setSelectedSubStageId] = useState<string | null>(subStageId || null);
  const [subStages, setSubStages] = useState<LeadSubStage[]>([]);
  const [loadingStages, setLoadingStages] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingStages(true);
    leadService
      .getSubStages({ campaignId, mainStatus })
      .then((rows) => {
        if (!cancelled) {
          setSubStages(rows);
          if (!rows.some((r) => r.id === selectedSubStageId)) {
            setSelectedSubStageId(null);
          }
        }
      })
      .catch(() => {
        if (!cancelled) setSubStages([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingStages(false);
      });
    return () => {
      cancelled = true;
    };
  }, [campaignId, mainStatus]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await leadService.updateLead(leadId, {
        status: mainStatus,
        subStageId: selectedSubStageId,
      });
      const stageName =
        subStages.find((s) => s.id === (updated.subStageId || selectedSubStageId))?.name ||
        updated.subStage?.name ||
        null;
      onUpdated({
        status: updated.status,
        subStageId: updated.subStageId || null,
        subStageName: stageName,
      });
      toast.success('Status updated');
      if (mainStatus === 'follow_up') onRequestFollowUp?.();
      onClose();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-muted rounded-xl px-3 py-3 space-y-3 mx-1">
      <div>
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Current</p>
        <p className="text-xs font-semibold text-foreground">
          {formatLeadStatusLabel(currentStatus)}
          {subStageName ? ` · ${subStageName}` : ''}
        </p>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Main Status</label>
        <select
          value={mainStatus}
          onChange={(e) => setMainStatus(e.target.value as LeadStatus)}
          className="mt-1 w-full rounded-lg border border-border bg-card px-3 py-2 text-xs text-foreground"
        >
          {LEAD_STATUS_CHANGE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Sub-Stage</label>
        {loadingStages ? (
          <div className="flex justify-center py-3">
            <LoadingSpinner size="sm" />
          </div>
        ) : subStages.length === 0 ? (
          <p className="text-[11px] text-muted-foreground mt-1">No sub-stages for this status.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {subStages.map((stage) => (
              <button
                key={stage.id}
                type="button"
                onClick={() => setSelectedSubStageId(stage.id)}
                className={`rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                  selectedSubStageId === stage.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card border border-border text-foreground hover:bg-muted'
                }`}
              >
                {stage.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="w-full bg-primary text-primary-foreground rounded-lg py-2 text-xs font-semibold disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save Status'}
      </button>
    </div>
  );
};

export default LeadStatusChangeMenu;
